import { useMutation, useQuery } from '@apollo/client';
import { css } from '@emotion/core';
import { Button, IconButton, MenuItem, Popper, PopperProps, TextField, Tooltip } from '@material-ui/core';
import { Cancel, Info } from '@material-ui/icons';
import { Autocomplete, RenderInputParams } from '@material-ui/lab';
import { isNil } from 'lodash';
import React from 'react';

import { PageDialogLink } from '../common/page-dialog-link';
import { formatSendTime, generateTimeOptions, parseSendTime } from '../common/timestamp';
import {
    DELETE_JOB_OUTREACH_SCHEDULE,
    JOB_OUTREACH_SCHEDULES,
    JobOutreachSchedule,
    SAVE_JOB_OUTREACH_SCHEDULE
} from '../graphql/queries/outreach';
import { useModal } from '../hooks/use-modal';
import { useSession } from '../hooks/use-session';
import { useSnackbar } from '../hooks/use-snackbar';
import { CellValue, DataPanel, DataPanelColumn } from './data-panel';
import { JobOutreachScheduleNowButton } from './job-outreach-schedule-now-button';
import { JobSearches } from './job-searches';

const styles = css`
    .MuiInput-underline:after {
        display: none;
    }

    .MuiInput-underline:before {
        display: none;
    }

    .MuiInputBase-input {
        font-size: 14px;
    }

    .MuiInputBase-root.Mui-disabled {
        color: inherit;
    }

    .MuiTableBody-root .MuiTableRow-root {
        .show-on-hover {
            opacity: 0;
            transition: opacity 0.2s ease-in-out;
        }

        &:hover {
            .show-on-hover {
                opacity: 1;
            }
        }

        .MuiButtonBase-root {
            margin: -10px 0;
        }

        .MuiTableCell-body:not(.actions) .inactive {
            opacity: 0.5;
        }

        .placeholder {
            font-style: italic;
            opacity: 0.75;
        }

        .actions-cell {
            display: flex;
            align-items: center;
            gap: 4px;
        }

        .send-time-cell {
            .MuiAutocomplete-endAdornment {
                top: calc(50% - 10px);
            }
        }
    }
`;

const autocompletePopperStyles = css`
    width: 150px !important;

    .MuiAutocomplete-listbox {
        max-height: 300px;
        overflow-y: auto;
        overflow-x: hidden;
    }

    .MuiAutocomplete-option {
        min-width: 0;
        padding: 8px 16px;
        white-space: nowrap;
    }
`;

const minSizeForPagination = 10;

export type JobScheduleUpdateData = Omit<
    JobOutreachSchedule,
    'user' | 'userId' | 'lastScheduledAt' | 'job' | 'id' | 'stats'
>;

export type ScheduleRowData = Omit<JobOutreachSchedule, 'id' | 'job' | 'stats'> &
    Partial<Pick<JobOutreachSchedule, 'id' | 'job' | 'stats'>>;

type Column =
    | 'client'
    | 'job'
    | 'recruiter'
    | 'count'
    | 'frequency'
    | 'sendTime'
    | 'active'
    | 'actions'
    | 'scheduled'
    | 'sent';

const allColumns: Array<DataPanelColumn<Column>> = [
    { id: 'client', label: 'Client' },
    { id: 'job', label: 'Job' },
    { id: 'recruiter', label: 'Recruiter' },
    { id: 'count', label: 'Count (per period)' },
    { id: 'frequency', label: 'Period length (days)' },
    { id: 'sendTime', label: 'Send time' },
    { id: 'active', label: 'Active' },
    { id: 'scheduled', label: 'Scheduled' },
    { id: 'sent', label: 'Sent' },
    { id: 'actions', label: '' }
];

// Time options from 8am to 6pm at 5-minute intervals
const timeOptions = generateTimeOptions();

interface OutreachSchedulesDataPanelProps {
    jobId?: string;
    newScheduleTemplate?: ScheduleRowData;
    username?: string;
    showStats?: boolean;
}

export const OutreachSchedulesDataPanel: React.FC<OutreachSchedulesDataPanelProps> = ({
    jobId,
    newScheduleTemplate,
    username,
    showStats = true
}) => {
    const { user } = useSession();
    const { setSnackbar } = useSnackbar();
    const { setAlert } = useModal();

    const whereClause: any = {};
    if (jobId) {
        whereClause.jobId = { _eq: jobId };
    }
    if (username) {
        const formattedUsername = username === 'me' ? user.username : username;
        whereClause.user = { username: { _eq: formattedUsername } };
    }

    const { data, refetch } = useQuery<{ jobOutreachSchedules: JobOutreachSchedule[] }>(JOB_OUTREACH_SCHEDULES, {
        variables: { where: whereClause }
    });

    const [saveSchedule] = useMutation<{}, { schedule: JobScheduleUpdateData }>(SAVE_JOB_OUTREACH_SCHEDULE);
    const [deleteSchedule] = useMutation<{}, { jobId: string; userId: string }>(DELETE_JOB_OUTREACH_SCHEDULE);

    const [editState, setEditState] = React.useState<Map<string, JobScheduleUpdateData>>(new Map());

    // Use id as key, or placeholder key for new schedules
    const getScheduleKey = (schedule: ScheduleRowData) =>
        schedule.id ?? `placeholder:${schedule.userId}:${schedule.jobId}`;

    const handleSaveSchedule = async (schedule: ScheduleRowData) => {
        const scheduleKey = getScheduleKey(schedule);
        const updates = editState.get(scheduleKey);
        if (!updates) return;

        const scheduleToSave = { ...updates };
        let deactivated = false;
        if (scheduleToSave.count <= 0) {
            scheduleToSave.count = schedule.count || 1;
            scheduleToSave.isActive = false;
            deactivated = true;
        }

        await saveSchedule({ variables: { schedule: scheduleToSave } });
        setSnackbar(deactivated ? 'Schedule paused (count must be at least 1)' : 'Schedule saved successfully');
        setEditState((prev) => {
            const newState = new Map(prev);
            newState.delete(scheduleKey);
            return newState;
        });
        refetch();
    };

    const handleDeleteSchedule = async (schedule: ScheduleRowData) => {
        const scheduleKey = getScheduleKey(schedule);
        await deleteSchedule({ variables: { jobId: schedule.jobId, userId: schedule.userId } });
        setSnackbar('Schedule deleted successfully');
        setEditState((prev) => {
            const newState = new Map(prev);
            newState.delete(scheduleKey);
            return newState;
        });
        refetch();
    };

    const handleFieldChange =
        (schedule: ScheduleRowData, field: keyof JobScheduleUpdateData) =>
        (event: React.ChangeEvent<HTMLInputElement>) => {
            const scheduleKey = getScheduleKey(schedule);

            const currentState = editState.get(scheduleKey) || {
                count: schedule.count,
                frequencyDays: schedule.frequencyDays,
                isActive: schedule.isActive,
                jobId: schedule.jobId,
                sendHour: schedule.sendHour,
                sendMinute: schedule.sendMinute
            };

            const value =
                field === 'count' || field === 'frequencyDays' || field === 'sendHour' || field === 'sendMinute'
                    ? parseInt(event.target.value, 10)
                    : event.target.value;

            setEditState((prev) => new Map(prev).set(scheduleKey, { ...currentState, [field]: value }));
        };

    const handleInfoClick = (schedule: ScheduleRowData) => () => {
        const count = getEditableValue(schedule, 'count');
        const frequencyDays = getEditableValue(schedule, 'frequencyDays');
        const isActive = getEditableValue(schedule, 'isActive');
        const sendHour = getEditableValue(schedule, 'sendHour') as number | undefined;
        const sendMinute = getEditableValue(schedule, 'sendMinute') as number | undefined;
        const hasExplicitSendTime = !isNil(sendHour) && !isNil(sendMinute);
        const sendTime = hasExplicitSendTime
            ? formatSendTime(sendHour, sendMinute)
            : 'best time to send calculated using historical response rate analysis';
        const inActivePrefix = isActive ? '' : 'If set to active (currently paused), ';
        const period = frequencyDays === 1 ? 'day' : `${frequencyDays} days`;
        setAlert(
            'Outreach Schedule',
            `${inActivePrefix}Titan will try to send ${count ?? schedule.count} outreach emails every ${period} at ${sendTime}.`
        );
    };

    const handleSendTimeChange = (schedule: ScheduleRowData) => (_1: React.ChangeEvent<{}>, value: string | null) => {
        const scheduleKey = getScheduleKey(schedule);

        const currentState = editState.get(scheduleKey) || {
            count: schedule.count,
            frequencyDays: schedule.frequencyDays,
            isActive: schedule.isActive,
            jobId: schedule.jobId,
            sendHour: schedule.sendHour,
            sendMinute: schedule.sendMinute
        };

        if (!value) {
            // Clear send time
            setEditState((prev) =>
                new Map(prev).set(scheduleKey, {
                    ...currentState,
                    sendHour: undefined,
                    sendMinute: undefined
                })
            );
            return;
        }

        const parsed = parseSendTime(value);
        if (parsed) {
            setEditState((prev) =>
                new Map(prev).set(scheduleKey, {
                    ...currentState,
                    sendHour: parsed.sendHour,
                    sendMinute: parsed.sendMinute
                })
            );
        }
    };

    const handleActiveChange = (schedule: ScheduleRowData) => (event: React.ChangeEvent<HTMLInputElement>) => {
        const scheduleKey = getScheduleKey(schedule);

        const currentState = editState.get(scheduleKey) || {
            count: schedule.count,
            frequencyDays: schedule.frequencyDays,
            isActive: schedule.isActive,
            jobId: schedule.jobId,
            sendHour: schedule.sendHour,
            sendMinute: schedule.sendMinute
        };

        setEditState((prev) =>
            new Map(prev).set(scheduleKey, { ...currentState, isActive: event.target.value === '1' })
        );
    };

    const schedules = data?.jobOutreachSchedules || [];

    const getEditableValue = (schedule: ScheduleRowData, field: keyof JobScheduleUpdateData) => {
        const scheduleKey = getScheduleKey(schedule);
        const updates = editState.get(scheduleKey);
        return updates?.[field] ?? schedule[field];
    };

    const isDirty = (schedule: ScheduleRowData): boolean => {
        const scheduleKey = getScheduleKey(schedule);
        const updates = editState.get(scheduleKey);
        if (!updates) return false;

        return (
            updates.count !== schedule.count ||
            updates.frequencyDays !== schedule.frequencyDays ||
            updates.isActive !== schedule.isActive ||
            updates.sendHour !== schedule.sendHour ||
            updates.sendMinute !== schedule.sendMinute
        );
    };

    const tableData = newScheduleTemplate ? [newScheduleTemplate, ...schedules] : schedules;

    const showClientColumn = !jobId;
    const showJobColumn = !jobId;
    const showRecruiterColumn = !username;

    const displayedColumns = allColumns.filter((col) => {
        if (col.id === 'client' && !showClientColumn) return false;
        if (col.id === 'job' && !showJobColumn) return false;
        if (col.id === 'recruiter' && !showRecruiterColumn) return false;
        if (col.id === 'scheduled' && !showStats) return false;
        if (col.id === 'sent' && !showStats) return false;
        return true;
    });

    const getCellValue =
        (column: Column) =>
        (row: ScheduleRowData): CellValue => {
            switch (column) {
                case 'client':
                    return row.job?.client?.name ?? '';
                case 'job':
                    return row.job?.title ?? '';
                case 'recruiter':
                    return row.user.name;
                case 'count':
                    return getEditableValue(row, 'count');
                case 'frequency':
                    return getEditableValue(row, 'frequencyDays');
                case 'sendTime':
                    return formatSendTime(
                        getEditableValue(row, 'sendHour') as number | undefined,
                        getEditableValue(row, 'sendMinute') as number | undefined
                    );
                case 'active':
                    return getEditableValue(row, 'isActive');
                case 'scheduled':
                    return row.stats?.[0]?.currentlyScheduled;
                case 'sent':
                    return row.stats?.[0]?.sentInPeriod;
                default:
                    return null;
            }
        };

    const getCellClassName = (row: ScheduleRowData) => {
        const isPlaceholder = !row.id;
        const dirty = isDirty(row);
        return dirty ? '' : isPlaceholder ? 'placeholder' : row.isActive ? '' : 'inactive';
    };

    const getCellFilterValue = {
        active: (value: CellValue) => {
            if (value === true) return 'Active';
            if (value === false) return 'Inactive';
            return 'Placeholder';
        }
    };

    const getCellRenderer = {
        actions: (_: CellValue, row: ScheduleRowData) => {
            const canEdit = user.id === row.userId;
            const isPlaceholder = !row.id;
            const dirty = isDirty(row);

            const infoButton = (
                <IconButton className="show-on-hover" onClick={handleInfoClick(row)}>
                    <Info />
                </IconButton>
            );

            const handleSave = () => handleSaveSchedule(row);
            const handleDelete = () => handleDeleteSchedule(row);

            const saveButton =
                canEdit && (dirty || isPlaceholder) ? (
                    <Button onClick={handleSave} disabled={getEditableValue(row, 'isActive') === undefined}>
                        Save
                    </Button>
                ) : null;

            const deleteButton =
                canEdit && !isPlaceholder && !dirty ? (
                    <Tooltip title="Delete schedule">
                        <IconButton onClick={handleDelete} className="show-on-hover">
                            <Cancel />
                        </IconButton>
                    </Tooltip>
                ) : null;

            const sendOutreachButton = canEdit ? (
                <span className="show-on-hover">
                    <JobOutreachScheduleNowButton jobId={row.jobId} jobStatus={row.job?.status} />
                </span>
            ) : null;

            return (
                <div className="actions-cell">
                    {saveButton}
                    {infoButton}
                    {sendOutreachButton}
                    {deleteButton}
                </div>
            );
        },
        active: (_: CellValue, row: ScheduleRowData) => {
            const canEdit = user.id === row.userId;
            const value = getEditableValue(row, 'isActive');
            const isPlaceholder = !row.id;
            const dirty = isDirty(row);

            const activeFieldOptions = [
                <MenuItem value="1" key="1">
                    Active
                </MenuItem>,
                <MenuItem value="0" key="0">
                    Paused
                </MenuItem>
            ];

            if (isPlaceholder && !dirty) {
                activeFieldOptions.push(
                    <MenuItem value="-1" key="-1">
                        Placeholder
                    </MenuItem>
                );
            }

            return (
                <TextField
                    value={isPlaceholder && !dirty ? '-1' : value ? '1' : '0'}
                    select={true}
                    onChange={canEdit ? handleActiveChange(row) : undefined}
                    disabled={!canEdit}
                    className={getCellClassName(row)}
                >
                    {activeFieldOptions}
                </TextField>
            );
        },
        count: (_: CellValue, row: ScheduleRowData) => {
            const canEdit = user.id === row.userId;
            const value = getEditableValue(row, 'count');
            return (
                <TextField
                    value={value ?? ''}
                    onChange={canEdit ? handleFieldChange(row, 'count') : undefined}
                    disabled={!canEdit}
                    type="number"
                    className={getCellClassName(row)}
                />
            );
        },
        frequency: (_: CellValue, row: ScheduleRowData) => {
            const canEdit = user.id === row.userId;
            const value = getEditableValue(row, 'frequencyDays');
            return (
                <TextField
                    value={value ?? ''}
                    onChange={canEdit ? handleFieldChange(row, 'frequencyDays') : undefined}
                    disabled={!canEdit}
                    type="number"
                    className={getCellClassName(row)}
                />
            );
        },
        job: (_: CellValue, row: ScheduleRowData) => {
            return (
                <PageDialogLink
                    url={`/job/${row.job.id}/searches`}
                    Component={JobSearches}
                    componentProps={{ id: row.job.id }}
                >
                    <span>{row.job.title}</span>
                </PageDialogLink>
            );
        },
        recruiter: (_: CellValue, row: ScheduleRowData) => {
            return <TextField value={row.user.name} disabled={true} className={getCellClassName(row)} />;
        },
        scheduled: (_: CellValue, row: ScheduleRowData) => {
            return (
                <Tooltip title="Currently scheduled">
                    <span>{row.stats?.[0]?.currentlyScheduled}</span>
                </Tooltip>
            );
        },
        sendTime: (_: CellValue, row: ScheduleRowData) => {
            const canEdit = user.id === row.userId;
            const sendHour = getEditableValue(row, 'sendHour') as number | undefined;
            const sendMinute = getEditableValue(row, 'sendMinute') as number | undefined;
            const value = formatSendTime(sendHour, sendMinute);
            const renderTime = (params: RenderInputParams) => (
                <TextField {...params} placeholder="Auto" className={getCellClassName(row)} />
            );
            const popper = (props: PopperProps) => <Popper {...props} css={autocompletePopperStyles} />;

            return (
                <Autocomplete
                    value={value || null}
                    onChange={canEdit ? handleSendTimeChange(row) : undefined}
                    options={timeOptions}
                    disabled={!canEdit}
                    freeSolo={true}
                    renderInput={renderTime}
                    PopperComponent={popper}
                    className="send-time-cell"
                />
            );
        },
        sent: (_: CellValue, row: ScheduleRowData) => {
            return (
                <Tooltip title={`Sent in last ${row.stats?.[0]?.frequencyDays} days`}>
                    <span>{row.stats?.[0]?.sentInPeriod}</span>
                </Tooltip>
            );
        }
    };

    const filterableColumns: Column[] = ['client', 'job', 'recruiter', 'active'];

    const sortableColumns: Column[] = [
        'client',
        'job',
        'recruiter',
        'count',
        'frequency',
        'sendTime',
        'active',
        'scheduled',
        'sent'
    ];

    const renderSummary = (filteredData: ScheduleRowData[]) => {
        const activeSchedules = filteredData.filter((s) => s.id && s.isActive).length;
        const inactiveSchedules = filteredData.filter((s) => s.id && !s.isActive).length;
        return (
            <div>
                Active: {activeSchedules} / Inactive: {inactiveSchedules}
            </div>
        );
    };

    return (
        <DataPanel
            css={styles}
            columns={displayedColumns}
            data={tableData}
            getCellValue={getCellValue}
            getCellRenderer={getCellRenderer}
            getCellFilterValue={getCellFilterValue}
            filterableColumns={filterableColumns}
            sortableColumns={sortableColumns}
            initialSortColumn="recruiter"
            initialSortDirection="asc"
            hidePagination={tableData?.length < minSizeForPagination}
            pageSize={100}
            renderSummary={renderSummary}
        />
    );
};
